import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import {
  FlatList,
  Image,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import {
  BG_STYLES,
  COLORS,
  COMMON_STYLES,
  DefaultProductImage,
  LazyImage,
  RightArrowIcon,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import SearchInput from 'app/components/reusable/SearchInput'
import useDebounce from 'app/hooks/useDebounce'
import useFetchDemoMaterialProducts from 'app/hooks/useFetchDemoMaterialProducts'
import { useLoadMoreOnEndReached } from 'app/hooks/useLoadMoreOnEndReached'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import {
  DemoProductSelectionScreenProps,
  ROUTES,
} from 'app/routes/types'
import { DemoMaterialProduct } from 'app/models/RequestFlow'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { useTranslation } from 'react-i18next'
import DemoProductVariantDrawer, {
  getActiveVariants,
} from 'app/components/demoMaterial/DemoProductVariantDrawer'

const DemoProductSelection = ({ navigation }: DemoProductSelectionScreenProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<DemoMaterialProduct | null>(null)
  const [showDrawer, setShowDrawer] = useState(false)
  const debouncedSearch = useDebounce(searchQuery)
  const listRef = useRef<FlatList<DemoMaterialProduct>>(null)
  const { mediaStyles } = useContext(ResponsiveContext)

  const apiName = useMemo(() => debouncedSearch.trim() || undefined, [debouncedSearch])

  const { products, loading, loadingNext, hasMore, error, fetchNext } =
    useFetchDemoMaterialProducts({
      name: apiName,
    })

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingNext) return
    await fetchNext()
  }, [fetchNext, hasMore, loadingNext])

  const paginationResetKey = useMemo(() => apiName ?? '', [apiName])

  const { onEndReached, onMomentumScrollBegin, onScroll } = useLoadMoreOnEndReached(
    loadMore,
    {
      enabled: hasMore && !loading,
      isLoading: loadingNext,
      listRef,
      resetKey: paginationResetKey,
    },
  )

  const listProducts = useMemo(
    () => products.filter(item => getActiveVariants(item).length > 0),
    [products],
  )

  const openProductDrawer = (item: DemoMaterialProduct) => {
    const activeVariants = getActiveVariants(item)
    if (activeVariants.length === 0) {
      ToastAndroid.show(t('demo_no_active_variants'), ToastAndroid.SHORT)
      return
    }
    setSelectedProduct(item)
    setShowDrawer(true)
  }

  const showInitialLoader = loading && products.length === 0 && !error
  const showLoadError = Boolean(error) && !loading && products.length === 0

  if (showInitialLoader) return <Spinner />

  return (
    <View style={COMMON_STYLES.flex1}>
      <View style={styles.searchWrap}>
        <SearchInput
          placeholder={t('demo_search_product')}
          value={searchQuery}
          onChange={setSearchQuery}
          inputContainerStyle={[mediaStyles({ desktopStyle: styles.desktop })]}
        />
      </View>
      {showLoadError ? (
        <View style={[styles.emptyState, COMMON_STYLES.flex1, mediaStyles({ desktopStyle: styles.desktop })]}>
          <Text style={styles.emptyText}>{t('demo_products_load_error')}</Text>
        </View>
      ) : (
        <FlatList
          ref={listRef}
          data={listProducts}
          keyExtractor={item => item.id.toString()}
          onScroll={onScroll}
          scrollEventThrottle={16}
          onMomentumScrollBegin={onMomentumScrollBegin}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.2}
          contentContainerStyle={[
            styles.listContainer,
            listProducts.length === 0 && styles.emptyListContainer,
            mediaStyles({ desktopStyle: styles.desktop }),
          ]}
          ListEmptyComponent={
            !loading && !showLoadError ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>{t('demo_products_empty')}</Text>
              </View>
            ) : null
          }
          ListFooterComponent={
            loadingNext ? (
              <View style={styles.footerLoader}>
                <Spinner type="in-screen" />
              </View>
            ) : null
          }
          renderItem={({ item }) => (
            <Pressable style={styles.card} onPress={() => openProductDrawer(item)}>
              <LazyImage
                imageUrl={item.image_url}
                fallBackIcon={
                  <Image
                    source={DefaultProductImage as ImageSourcePropType}
                    style={styles.productImage}
                  />
                }
                imgStyles={styles.productImage}
              />
              <View style={[COMMON_STYLES.flex1, { rowGap: 2 }]}>
                <Text style={styles.brand}>{item.brand?.name || '-'}</Text>
                <Text style={styles.name}>{item.name}</Text>
              </View>
              <RightArrowIcon strokeColor="black" width={20} />
            </Pressable>
          )}
        />
      )}
      <DemoProductVariantDrawer
        visible={showDrawer}
        product={selectedProduct}
        onClose={() => setShowDrawer(false)}
        onContinue={(product, variant, quantity) => {
          if (!variant?.id) {
            ToastAndroid.show(t('demo_select_product_variant'), ToastAndroid.SHORT)
            return
          }
          setShowDrawer(false)
          navigation.navigate(ROUTES.DEMO_CREATE_REQUEST, {
            draft: {
              product_variant_id: variant.id,
              product_brand: product.brand?.name,
              product_name: product.name,
              product_variant_label: variant.attributes_name.trim(),
              product_image: product.image_url,
              units: quantity,
            },
          })
        }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  searchWrap: {
    padding: 8,
  },
  card: {
    ...BG_STYLES.bgWhite,
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES['p3/2'],
    borderTopColor: COLORS.neutral20,
    borderTopWidth: 1,
  },
  productImage: {
    width: 64,
    height: 64,
    marginRight: 14,
  },
  brand: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
  },
  name: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
  },
  listContainer: {
    paddingBottom: 24,
    backgroundColor: COLORS.white,
    flexGrow: 1,
  },
  emptyListContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  emptyState: {
    ...COMMON_STYLES.p2,
    ...COMMON_STYLES.alignItemsCenter,
  },
  emptyText: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.TEXT2,
    ...COMMON_STYLES.textCenter,
  },
  footerLoader: {
    minHeight: 56,
    ...COMMON_STYLES.justifyCenter,
    ...COMMON_STYLES.alignItemsCenter,
  },
  desktop: {
    maxWidth: '70%',
    width: '100%',
    alignSelf: 'center',
  },
})

export default DemoProductSelection
